import {
  messageListResponseSchema,
  messageResponseSchema,
  type Message,
  type MessageListResponse,
} from '@chatapp-inbox/shared';
import { request } from './http';

const base = (conversationId: string): string =>
  `/conversations/${encodeURIComponent(conversationId)}/messages`;

export interface ListMessagesParams {
  conversationId: string;
  /** `nextCursor` da página anterior (mensagens mais antigas); ausente = as mais recentes. */
  before?: string | null;
  limit?: number;
  signal?: AbortSignal;
}

/** Mensagens da conversa, mais recentes primeiro POR PÁGINA, cada página em ordem cronológica. */
export function listMessages(params: ListMessagesParams): Promise<MessageListResponse> {
  return request(base(params.conversationId), {
    query: { before: params.before, limit: params.limit },
    schema: messageListResponseSchema,
    ...(params.signal ? { signal: params.signal } : {}),
  });
}

/**
 * Envia texto. `clientId` torna o pedido IDEMPOTENTE: repetir com o mesmo `clientId` (ex.: a rede
 * caiu e não se sabe se chegou) devolve a mensagem já criada em vez de duplicar.
 */
export async function sendMessage(params: {
  conversationId: string;
  /** Texto, ou a legenda do arquivo (pode ser vazia quando há `attachmentId`). */
  text: string;
  clientId: string;
  /** Arquivo já enviado (`uploadAttachment`) a mandar nesta mensagem. */
  attachmentId?: string;
}): Promise<Message> {
  const { message } = await request(base(params.conversationId), {
    method: 'POST',
    body: {
      text: params.text,
      clientId: params.clientId,
      ...(params.attachmentId ? { attachmentId: params.attachmentId } : {}),
    },
    schema: messageResponseSchema,
  });
  return message;
}

/**
 * Sussurro: uma mensagem que só a equipe vê (nunca vai ao cliente). `clientId` a torna idempotente, como no envio comum.
 * Devolve a mensagem já gravada (não há "enviando": ela não passa pela fila de saída).
 */
export async function sendWhisper(params: {
  conversationId: string;
  text: string;
  clientId: string;
}): Promise<Message> {
  const { message } = await request(base(params.conversationId), {
    method: 'POST',
    body: { text: params.text, clientId: params.clientId, internal: true },
    schema: messageResponseSchema,
  });
  return message;
}

/** Reenvia uma mensagem de saída que terminou em erro. */
export async function retryMessage(conversationId: string, messageId: string): Promise<Message> {
  const { message } = await request(
    `${base(conversationId)}/${encodeURIComponent(messageId)}/retry`,
    { method: 'POST', schema: messageResponseSchema },
  );
  return message;
}
